<script lang="ts" setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNotificationsStore } from '@/store/notifications'
import { notificationError } from '@/app/core/util/notificationUtils'
import { createEmployeeReentryRequest } from '@/app/core/services/employees'
import {
  DModal,
  DTextField,
  DToggleField,
  InfoCircleIcon,
  DLoading
} from 'front-library'

interface Props {
  open: boolean
  employeeId?: string
  employeeCode?: string
  employeeName?: string
  recognitionDate?: string
}

interface Emits {
  (e: 'close'): void
  (e: 'success'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { t } = useI18n()
const notifications = useNotificationsStore()

const loading = ref(false)
const messageLoading = ref('')

const reentryForm = ref({
  entry_date: '',
  recognition_date: '',
  keep_salary: false,
  keep_bank_accounts: false,
  keep_accounting_distribution: false,
  keep_education_levels: false,
  keep_relatives: false,
  keep_identifications: false
})

watch(
  () => props.open,
  isOpen => {
    if (isOpen) {
      const today = new Date()
      const formattedDate = today.toISOString().split('T')[0]
      reentryForm.value.entry_date = formattedDate

      if (props.recognitionDate) {
        reentryForm.value.recognition_date = formatDate(props.recognitionDate)
      }
    } else {
      resetForm()
    }
  }
)

const formatDate = (dateString: string) => {
  if (!dateString) return ''

  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateString
  }

  try {
    const date = new Date(dateString)
    return date.toISOString().split('T')[0]
  } catch (error) {
    console.error('Error formatting date:', dateString, error)
    return ''
  }
}

watch(
  () => props.recognitionDate,
  newRecognitionDate => {
    if (newRecognitionDate) {
      reentryForm.value.recognition_date = formatDate(newRecognitionDate)
    }
  },
  { immediate: true }
)

watch(
  () => [props.employeeId, props.recognitionDate],
  ([newEmployeeId, newRecognitionDate]) => {
    if (newRecognitionDate) {
      reentryForm.value.recognition_date = formatDate(newRecognitionDate)
    }
  }
)

/* Funciones */
const closeModal = () => {
  emit('close')
  resetForm()
}

const resetForm = () => {
  reentryForm.value = {
    entry_date: '',
    recognition_date: '',
    keep_salary: false,
    keep_bank_accounts: false,
    keep_accounting_distribution: false,
    keep_education_levels: false,
    keep_relatives: false,
    keep_identifications: false
  }
}

const submitReentry = async () => {
  if (!props.employeeId) {
    notifications.addNotification({
      title: t('notification.error'),
      text: t('employees.reentries.validation.employeeRequired'),
      type: 'error',
      time: 10000
    })
    return
  }

  if (!reentryForm.value.entry_date || !reentryForm.value.recognition_date) {
    notifications.addNotification({
      title: t('employees.reentries.notification.error'),
      text: t('employees.reentries.validation.requiredFields'),
      type: 'error',
      time: 10000
    })
    return
  }

  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const success = await createEmployeeReentryRequest({
      employeeId: props.employeeId || '',
      entryDate: reentryForm.value.entry_date,
      recognitionDate: reentryForm.value.recognition_date,
      keepSalary: reentryForm.value.keep_salary,
      keepBankAccounts: reentryForm.value.keep_bank_accounts,
      keepAccountingDistribution:
        reentryForm.value.keep_accounting_distribution,
      keepEducationLevels: reentryForm.value.keep_education_levels,
      keepRelatives: reentryForm.value.keep_relatives,
      keepIdentifications: reentryForm.value.keep_identifications
    })

    if (success) {
      notifications.addNotification({
        title: t('employees.reentries.notification.success'),
        type: 'success',
        time: 10000
      })

      emit('success')
      closeModal()
    } else {
      notifications.addNotification({
        title: t('employees.reentries.notification.error'),
        type: 'error',
        time: 10000
      })
    }
  } catch (error: any) {
    if (error?.code === 'ERR_BAD_REQUEST' || error?.code === 'INTERNAL_ERROR') {
      notifications.addNotification({
        title: t('employees.reentries.notification.error'),
        type: 'error',
        time: 10000
      })
    } else {
      notificationError(error, 'employee-reentries', t, notifications, loading)
    }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <!-- Main Modal -->
  <DModal :open="open" :isFooter="false" @closeModal="closeModal">
    <template v-slot:form>
      <DLoading v-show="loading" :message="messageLoading" />
      <div class="min-h-[500px]">
        <div class="w-full flex flex-col items-center">
          <div
            class="flex items-center justify-center rounded-full h-14 w-14 mb-4 bg-blue-100">
            <InfoCircleIcon class="h-8 w-8 text-blue-500" />
          </div>
          <h1
            class="text-black text-xl text-center mb-5"
            v-html="t('employees.reentries.create')" />
        </div>

        <div class="space-y-4">
          <p class="text-sm text-gray-600 text-center py-2">
            {{
              t('employees.reentries.modal.description', {
                employeeCode: props.employeeCode,
                employeeName: props.employeeName
              })
            }}
          </p>

          <!-- Formulario de reingreso -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DTextField
              type="date"
              :label="t('employees.reentries.form.entryDate')"
              v-model="reentryForm.entry_date"
              :required="true" />

            <DTextField
              type="date"
              :label="t('employees.reentries.form.recognitionDate')"
              v-model="reentryForm.recognition_date"
              :required="true" />
          </div>

          <div class="space-y-3 py-4">
            <DToggleField
              :label="t('employees.reentries.form.keepSalary')"
              v-model="reentryForm.keep_salary" />

            <DToggleField
              :label="t('employees.reentries.form.keepBankAccounts')"
              v-model="reentryForm.keep_bank_accounts" />

            <DToggleField
              :label="t('employees.reentries.form.keepAccountingDistribution')"
              v-model="reentryForm.keep_accounting_distribution" />

            <DToggleField
              :label="t('employees.reentries.form.keepEducationLevels')"
              v-model="reentryForm.keep_education_levels" />

            <DToggleField
              :label="t('employees.reentries.form.keepRelatives')"
              v-model="reentryForm.keep_relatives" />

            <DToggleField
              :label="t('employees.reentries.form.keepIdentifications')"
              v-model="reentryForm.keep_identifications" />
          </div>

          <!-- Action Buttons -->
          <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              @click="closeModal"
              class="hover:bg-gray-600 hover:text-white focus:ring-gray-200 inline-flex text-sm justify-center items-center py-1.5 px-10 border border-gray-300 shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
              v-html="t('form.button.cancel')" />

            <button
              @click="submitReentry"
              :disabled="loading"
              class="bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed inline-flex text-sm justify-center items-center py-1.5 px-10 shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
              v-html="t('form.button.confirm')" />
          </div>
        </div>
      </div>
    </template>
  </DModal>
</template>
